import React, { useCallback, useEffect, useMemo, useState } from 'react';

import OriginTracer from '@atlassiansox/origin-tracing';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import type {
	OnCompleteCallback,
	OnErrorCallback,
	OnMessageCallback,
} from '@atlassian/commerce-experience-sdk';
import CommerceExperience from '@atlassian/commerce-experience-sdk-react';

import type { Product } from './PlaceOrderLite';

export const PRODUCT_TO_ORIGIN: Record<Product, string> = {
	['confluence']: 'confluence',
	['jira']: 'jira',
	['jira-product-discovery']: 'jira',
	['jira-service-management']: 'jira',
};

const styles = cssMap({
	sdkLoading: {
		height: '1px',
		visibility: 'hidden',
		position: 'absolute',
	},
});

function CommerceExperienceContainer({
	isLoading,
	children,
}: {
	children: React.ReactNode;
	isLoading: boolean;
}) {
	return (
		<>
			{isLoading ? (
				<Stack space="space.400">
					<Skeleton width="100%" height="32px" isShimmering />
					<Skeleton width="100%" height="32px" isShimmering />
				</Stack>
			) : null}
			{/*
				Visually hide the iframe while it's loading, but keep it in the DOM to ensure
				that it loads.
			*/}
			<Box xcss={isLoading ? styles.sdkLoading : undefined} aria-hidden={isLoading}>
				{children}
			</Box>
		</>
	);
}

export function CommerceEmbed({
	actionsAlignment,
	href,
	onComplete,
	onError,
	onMessage,
	product,
	primaryAction,
	secondaryAction,
	onSecondaryAction,
	trialTrigger,
	trialType,
}: {
	actionsAlignment?: 'vertical' | 'horizontal';
	/** This is the iframe that will be loaded, usually requested through the entitlement experience capability `placeOrderLite` */
	href: string;
	/** A callback function that will be called when the flow within the iframe is completed successfully. */
	onComplete: OnCompleteCallback;
	/** A callback function that will be called when there is an error in the flow within the iframe. */
	onError: OnErrorCallback;
	/** A callback function that will be called when specific state changes occur. The contract for the message data needs to be defined per-use case */
	onMessage?: OnMessageCallback;
	onSecondaryAction?: () => void;
	primaryAction?: string;
	/** Used for tracing */
	product: Product;
	secondaryAction?: string;
	/** Should be used ff starting a trial that is not counted as triggered by UI */
	trialTrigger?: string;
	/** Should be used ff starting a trial that is not counted as a manual trial */
	trialType?: string;
}): React.JSX.Element {
	const { colorMode } = useThemeObserver();

	const origin = useMemo(
		() => new OriginTracer({ product: PRODUCT_TO_ORIGIN[product] }),
		[product],
	);
	const embeddedUrl = useMemo(() => origin.addToUrl(href), [href, origin]);

	const [isLoadingSdk, setIsLoadingSdk] = useState(true);

	const handleOnLoad = useCallback(() => {
		setIsLoadingSdk(false);
	}, []);

	useEffect(() => {
		if (!isLoadingSdk) {
			return;
		}

		/**
		 * The Commerce SDK iframe is hidden initially while we wait for it to load.
		 * But, hiding an iframe on the page _might_ lead to the browser preventing the iframe from loading.
		 * So, we always set the loading state to false after 5 seconds just in case.
		 */
		const timeout = setTimeout(() => {
			setIsLoadingSdk(false);
		}, 5000);

		return () => clearTimeout(timeout);
	}, [isLoadingSdk, setIsLoadingSdk]);

	const configuration = useMemo(() => {
		if (!embeddedUrl) {
			return {};
		}

		const config = Object.fromEntries(new URL(embeddedUrl).searchParams);

		if (colorMode === 'dark') {
			config.theme = 'dark';
		}

		if (primaryAction) {
			config.primaryAction = primaryAction;
		}

		if (secondaryAction) {
			config.secondaryAction = secondaryAction;
		}

		if (actionsAlignment) {
			config.actionsAlignment = actionsAlignment;
		}

		if (trialTrigger) {
			config.trialTrigger = trialTrigger;
		}

		if (trialType) {
			config.trialType = trialType;
		}

		return config;
	}, [
		embeddedUrl,
		colorMode,
		primaryAction,
		secondaryAction,
		actionsAlignment,
		trialTrigger,
		trialType,
	]);

	const onMessageHandler: OnMessageCallback = useCallback(
		(message) => {
			if ('secondaryActionClicked' in message && onSecondaryAction) {
				onSecondaryAction();
			}

			onMessage?.(message);
		},
		[onSecondaryAction, onMessage],
	);

	return (
		<CommerceExperienceContainer isLoading={isLoadingSdk}>
			<CommerceExperience
				url={embeddedUrl}
				configuration={configuration}
				onError={onError}
				onComplete={onComplete}
				onMessage={onMessageHandler}
				onLoad={handleOnLoad}
			/>
		</CommerceExperienceContainer>
	);
}
